/**
 * Canonical pagination types (Laravel-style).
 *
 * Goal: provide reusable pagination contracts so endpoints don't need to duplicate
 * `Entities` structures (current_page, last_page, links, etc.) under different names.
 *
 * Notes:
 * - Some endpoints have `from/to` as `number | null` (e.g. teachers, departments).
 * - Others use strict `number` for `from/to`.
 */

export interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

/**
 * Generic paginated envelope for list endpoints (Laravel-style).
 *
 * TItem: element type of `data`.
 * TFromTo: allows capturing both `number` and `number | null` variants.
 */
export interface Paginated<TItem, TFromTo extends number | null = number> {
  current_page: number;
  data: TItem[];

  first_page_url: string;

  from: TFromTo;
  last_page: number;
  last_page_url: string;

  links: PaginationLink[];

  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;

  to: TFromTo;
  total: number;
}

/** Convenience alias for the common strict variant. */
export type PaginatedStrict<TItem> = Paginated<TItem, number>;

/** Convenience alias for the nullable `from/to` variant. */
export type PaginatedNullable<TItem> = Paginated<TItem, number | null>;

/**
 * Small helper for UI state: clamp page into [1..last_page].
 * Pure function; safe to use in components that guard against inconsistent API.
 */
export const clampPage = (page: number, lastPage: number): number => {
  if (!Number.isFinite(page)) return 1;
  if (!Number.isFinite(lastPage) || lastPage < 1) return 1;
  if (page < 1) return 1;
  if (page > lastPage) return lastPage;
  return page;
};